import * as React from 'react';
import { PanelLeft } from 'lucide-react';
import { useIsMobile } from '../../../hooks/use-mobile';
import { cn } from '../../../lib/utils';
import styles from './sidebar.module.scss';

const SIDEBAR_STORAGE_KEY = 'sidebar_state';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b'; // Cmd/Ctrl + B để đóng/mở sidebar

type SidebarContextProps = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

export function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }
  return context;
}

interface SidebarProviderProps extends React.ComponentProps<'div'> {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const SidebarProvider = React.forwardRef<HTMLDivElement, SidebarProviderProps>(
  (
    {
      defaultOpen = true,
      open: openProp,
      onOpenChange: setOpenProp,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const isMobile = useIsMobile();
    const [openMobile, setOpenMobile] = React.useState(false);

    // Get initial state from localStorage
    const getInitialState = () => {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      if (stored !== null) {
        return stored === 'true';
      }
      return defaultOpen;
    };

    const [_open, _setOpen] = React.useState(getInitialState);
    const open = openProp ?? _open;

    const setOpen = React.useCallback(
      (value: boolean | ((value: boolean) => boolean)) => {
        const openState = typeof value === 'function' ? value(open) : value;
        if (setOpenProp) {
          setOpenProp(openState);
        } else {
          _setOpen(openState);
        }

        localStorage.setItem(SIDEBAR_STORAGE_KEY, openState.toString());
      },
      [setOpenProp, open]
    );

    // Toggle sidebar
    const toggleSidebar = React.useCallback(() => {
      return isMobile ? setOpenMobile(prev => !prev) : setOpen(prev => !prev);
    }, [isMobile, setOpen]);

    // Keyboard shortcut feature
    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (
          event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
          (event.metaKey || event.ctrlKey)
        ) {
          event.preventDefault();
          toggleSidebar();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [toggleSidebar]);

    const state = open ? 'expanded' : 'collapsed';

    const contextValue = React.useMemo<SidebarContextProps>(
      () => ({
        state,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
      }),
      [state, open, setOpen, isMobile, openMobile, toggleSidebar]
    );

    return (
      <SidebarContext.Provider value={contextValue}>
        <div
          ref={ref}
          className={cn(styles.sidebarWrapper, className)}
          data-state={state}
          {...props}
        >
          {children}
        </div>
      </SidebarContext.Provider>
    );
  }
);
SidebarProvider.displayName = 'SidebarProvider';

interface SidebarProps extends React.ComponentProps<'div'> {
  side?: 'left' | 'right';
  collapsible?: 'icon' | 'none';
}

export const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
  ({ side = 'left', collapsible = 'icon', className, children, ...props }, ref) => {
    const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

    if (collapsible === 'none') {
      return (
        <div
          className={cn(styles.sidebar, styles.sidebarNonCollapsible, className)}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      );
    }

    // Mobile responsive version
    if (isMobile) {
      return (
        <>
          {openMobile && (
            <div className={styles.mobileOverlay} onClick={() => setOpenMobile(false)} />
          )}
          <div
            className={cn(
              styles.sidebar,
              styles.sidebarMobile,
              openMobile && styles.sidebarMobileOpen,
              className
            )}
            data-mobile="true"
            data-side={side}
            {...props}
          >
            {children}
          </div>
        </>
      );
    }

    // Desktop version
    return (
      <div
        ref={ref}
        className={cn(
          styles.sidebar,
          styles.sidebarDesktop,
          className,
          "sidebarDesktop"
        )}
        data-state={state}
        data-collapsible={collapsible}
        data-side={side}
        {...props}
      >
        <div className={styles.sidebarContent}>
          {children}
        </div>
      </div>
    );
  }
);
Sidebar.displayName = 'Sidebar';

interface SidebarTriggerProps extends React.ComponentProps<'button'> { }

export const SidebarTrigger = React.forwardRef<HTMLButtonElement, SidebarTriggerProps>(
  ({ className, onClick, ...props }, ref) => {
    const { toggleSidebar } = useSidebar();

    return (
      <button
        ref={ref}
        className={cn(styles.sidebarTrigger, className)}
        onClick={(event) => {
          onClick?.(event);
          toggleSidebar();
        }}
        {...props}
      >
        <PanelLeft className={styles.sidebarTriggerIcon} />
        <span className={styles.srOnly}>Toggle Sidebar</span>
      </button>
    );
  }
);
SidebarTrigger.displayName = 'SidebarTrigger';

export const SidebarHeader = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(styles.sidebarHeader, className)}
        {...props}
      />
    );
  }
);
SidebarHeader.displayName = 'SidebarHeader';

export const SidebarFooter = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(styles.sidebarFooter, className)}
        {...props}
      />
    );
  }
);
SidebarFooter.displayName = 'SidebarFooter';

export const SidebarContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(styles.sidebarContentInner, className)}
        {...props}
      />
    );
  }
);
SidebarContent.displayName = 'SidebarContent';

export const SidebarGroup = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(styles.sidebarGroup, className)}
        {...props}
      />
    );
  }
);
SidebarGroup.displayName = 'SidebarGroup';

export const SidebarGroupLabel = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(styles.sidebarGroupLabel, className)}
        {...props}
      />
    );
  }
);
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

export const SidebarGroupContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(styles.sidebarGroupContent, className)}
      {...props}
    />
  )
);
SidebarGroupContent.displayName = 'SidebarGroupContent';

export const SidebarMenu = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      className={cn(styles.sidebarMenu, className)}
      {...props}
    />
  )
);
SidebarMenu.displayName = 'SidebarMenu';

export const SidebarMenuItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
  ({ className, ...props }, ref) => (
    <li
      ref={ref}
      className={cn(styles.sidebarMenuItem, className)}
      {...props}
    />
  )
);
SidebarMenuItem.displayName = 'SidebarMenuItem';

interface SidebarMenuButtonProps extends React.ComponentProps<'button'> {
  isActive?: boolean;
  tooltip?: string;
}

export const SidebarMenuButton = React.forwardRef<HTMLButtonElement, SidebarMenuButtonProps>(
  ({ className, isActive = false, tooltip, children, ...props }, ref) => {
    const { state } = useSidebar();
    const [showTooltip, setShowTooltip] = React.useState(false);
    const [tooltipPosition, setTooltipPosition] = React.useState({ top: 0 });

    const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
      setShowTooltip(true);
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPosition({ top: rect.top + rect.height / 2 });
    };

    return (
      <div className={styles.sidebarMenuButtonWrapper}>
        <button
          ref={ref}
          className={cn(
            styles.sidebarMenuButton,
            isActive && styles.sidebarMenuButtonActive,
            className
          )}
          data-active={isActive}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => setShowTooltip(false)}
          {...props}
        >
          {children}
        </button>
        {tooltip && state === 'collapsed' && showTooltip && (
          <div
            className={styles.sidebarTooltip}
            style={{
              top: `${tooltipPosition.top}px`,
              transform: 'translateY(-50%)',
            }}
          >
            {tooltip}
          </div>
        )}
      </div>
    );
  }
);
SidebarMenuButton.displayName = 'SidebarMenuButton';

export const SidebarMenuSub = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      className={cn(styles.sidebarMenuSub, className)}
      {...props}
    />
  )
);
SidebarMenuSub.displayName = 'SidebarMenuSub';

export const SidebarMenuSubItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
  ({ className, ...props }, ref) => (
    <li
      ref={ref}
      className={cn(styles.sidebarMenuSubItem, className)}
      {...props}
    />
  )
);
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem';

interface SidebarMenuSubButtonProps extends React.ComponentProps<'button'> {
  isActive?: boolean;
}

export const SidebarMenuSubButton = React.forwardRef<HTMLButtonElement, SidebarMenuSubButtonProps>(
  ({ className, isActive = false, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          styles.sidebarMenuSubButton,
          isActive && styles.sidebarMenuSubButtonActive,
          className
        )}
        data-active={isActive}
        {...props}
      >
        {children}
      </button>
    );
  }
);
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';

export const SidebarSeparator = React.forwardRef<HTMLHRElement, React.ComponentProps<'hr'>>(
  ({ className, ...props }, ref) => {
    return (
      <hr
        ref={ref}
        className={cn(styles.sidebarSeparator, className)}
        {...props}
      />
    );
  }
);
SidebarSeparator.displayName = 'SidebarSeparator';
